import React, { useEffect, useMemo, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { QRCodeSVG } from 'qrcode.react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Printer } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Common label / Avery sizes (width × height in inches).
// `printable_inset` is the hardware "dead zone" the printer cannot reach
// (thermal heads on Brother / Zebra have ~1mm = 0.04" they physically can't
// print on each edge). Content is inset by this much so it never gets clipped.
export const STICKER_SIZES = [
  // Generic sizes
  { id: '1x1',     label: '1" × 1" (square)',                width: 1.00, height: 1.00, printable_inset: 0.02 },
  { id: '2x0_5',   label: '2" × 1/2" (compact)',             width: 2.00, height: 0.50, printable_inset: 0.02 },
  { id: '1_142x3_5', label: '1-1/7" × 3-1/2" (portrait)',     width: 1.142857, height: 3.50, printable_inset: 0.02 },
  { id: '2x1',     label: '2" × 1" (Avery 5167)',            width: 2.00, height: 1.00, printable_inset: 0.02 },
  { id: '2_625x1', label: '2.625" × 1" (Avery 5160)',        width: 2.625, height: 1.00, printable_inset: 0.02 },
  { id: '2x2',     label: '2" × 2" (square)',                width: 2.00, height: 2.00, printable_inset: 0.02 },
  { id: '3x2',     label: '3" × 2"',                         width: 3.00, height: 2.00, printable_inset: 0.02 },
  { id: '4x2',     label: '4" × 2" (shipping)',              width: 4.00, height: 2.00, printable_inset: 0.02 },
  { id: '4x2_428', label: '4" × 2-3/7"',                     width: 4.00, height: 2.428571, printable_inset: 0.02 },
  { id: '4x3',     label: '4" × 3"',                         width: 4.00, height: 3.00, printable_inset: 0.02 },
  { id: '4x6',     label: '4" × 6" (large shipping)',        width: 4.00, height: 6.00, printable_inset: 0.02 },
  // iter78x — Brother QL-series thermal labels. Dimensions match Brother
  // driver media exactly. printable_inset = 0.06" (~1.5mm) for the thermal
  // head dead zone so the QR/title never gets cut off at the edge.
  { id: 'brother_dk1202', label: 'Brother DK-1202 (2.4" × 3.9")',     width: 2.40, height: 3.90, printable_inset: 0.06 },
  { id: 'brother_dk1208', label: 'Brother DK-1208 (1.4" × 3.5")',     width: 1.40, height: 3.50, printable_inset: 0.06 },
  { id: 'brother_dk1209', label: 'Brother DK-1209 (1.1" × 2.4")',     width: 1.14, height: 2.43, printable_inset: 0.06 },
  { id: 'brother_dk1241', label: 'Brother DK-1241 (4" × 6")',         width: 4.00, height: 6.00, printable_inset: 0.06 },
  { id: 'brother_dk1247', label: 'Brother DK-1247 (4.07" × 6.4")',    width: 4.07, height: 6.40, printable_inset: 0.06 },
  { id: 'brother_dk2205', label: 'Brother DK-2205 (2.4" continuous)', width: 2.40, height: 4.00, printable_inset: 0.06 },
  { id: 'brother_dk2225', label: 'Brother DK-2225 (1.5" continuous)', width: 1.50, height: 3.00, printable_inset: 0.06 },
];

const PrintLabelDialog = ({ open, onOpenChange, item, items, labelType = 'Item' }) => {
  const { api } = useAuth();
  const [sizeId, setSizeId] = useState('2x1');
  const [copies, setCopies] = useState(1);
  // iter35: 3-way QR mode — 'info', 'restock', or 'both'
  const [qrMode, setQrMode] = useState('info');
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('default');
  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === templateId) || null,
    [templates, templateId]
  );

  // Fetch templates whenever the dialog is opened so the user is prompted with
  // saved template choices that match this entity type.
  useEffect(() => {
    if (!open) return;
    let active = true;
    api()
      .get('/label-templates')
      .then((r) => { if (active) setTemplates(r.data || []); })
      .catch(() => { if (active) setTemplates([]); });
    return () => { active = false; };
  }, [open, api]);

  // Reset the template choice if the label type changes
  useEffect(() => { setTemplateId('default'); }, [labelType, open]);

  const size = useMemo(
    () => STICKER_SIZES.find(s => s.id === sizeId) || STICKER_SIZES[0],
    [sizeId]
  );

  // Support both single-item and bulk modes
  const itemList = items && items.length ? items : (item ? [item] : []);
  const previewItem = itemList[0];
  if (!previewItem) return null;
  const isBulk = itemList.length > 1;

  // iter78u — ALWAYS derive the QR target from the CURRENT browser origin so
  // labels scan correctly regardless of where they were printed (preview vs
  // production) or what stale URL was stamped onto `barcode_or_qr_value` at
  // item-creation time. The old code preferred the saved URL which caused QR
  // codes to point at the backend / preview environment on production prints.
  const detailUrlFor = (it) => {
    if (it.id && typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}/items/view/${it.id}`;
    }
    // Last-resort fallback for legacy rows with no id
    const base = it.barcode_or_qr_value || '';
    if (base.startsWith('http')) return base;
    return base || it.item_sku;
  };
  const infoUrlFor = (it) => detailUrlFor(it);
  const restockQrValueFor = (it) => {
    const url = detailUrlFor(it);
    if (url.startsWith('http')) {
      const sep = url.includes('?') ? '&' : '?';
      return `${url}${sep}action=restock`;
    }
    return `${url}#restock`;
  };
  const infoQrValue = infoUrlFor(previewItem);
  const restockQrValue = restockQrValueFor(previewItem);

  // Dual QR only makes sense on landscape labels with URL-encoded info QR (which
  // we now guarantee whenever the item has an id).
  const dualSupported = size.width >= 2 && infoQrValue.startsWith('http');
  const showDual = qrMode === 'both' && dualSupported;
  const showInfoOnly = qrMode === 'info' || (qrMode === 'both' && !dualSupported);
  const showRestockOnly = qrMode === 'restock' && infoQrValue.startsWith('http');
  const primaryQrValue = showRestockOnly ? restockQrValue : infoQrValue;
  const primaryQrLabel = showRestockOnly ? 'RESTOCK' : 'INFO';

  // Layout heuristics: square -> stack QR + text; landscape -> QR left, text right
  const isLandscape = size.width >= size.height * 1.4;

  const handlePrint = () => {
    const w = window.open('', '_blank', 'width=600,height=600');
    if (!w) {
      window.alert('Please allow pop-ups to print labels.');
      return;
    }
    // Adaptive padding: shrink for tiny labels so the QR + text fit cleanly.
    // 0.08in default; for sub-1in dimensions cap padding at 8% of that dim.
    const pad = Math.max(0.02, Math.min(0.08, Math.min(size.width, size.height) * 0.08));
    // Available inner area in inches after symmetric padding
    const innerW = Math.max(0.1, size.width - pad * 2);
    const innerH = Math.max(0.1, size.height - pad * 2);
    // QR sizing: cap to whichever inner dimension is smaller so it never gets cut off.
    // Landscape → QR sized off inner height; Portrait/square → QR sized off inner width.
    const baseQrIn = isLandscape
      ? Math.min(innerH * 0.92, innerW * 0.6)
      : Math.min(innerW * 0.7, innerH * 0.55);
    const qrPx = Math.max(24, Math.round(baseQrIn * 96 * (showDual ? 0.85 : 1)));

    const resolveField = (field, it) => {
      if (field.field_type === 'text') {
        return field.static_value || field.field_name;
      }
      if (field.field_type === 'linked') {
        const v = it?.[field.data_path];
        return v == null || v === '' ? '—' : String(v);
      }
      if (field.field_type === 'barcode') {
        return it.item_sku || '';
      }
      return ''; // qr handled separately below
    };

    const renderTemplateLabel = (it) => {
      const blocks = (selectedTemplate?.fields || []).map((f) => {
        if (f.field_type === 'qr') {
          const val = infoUrlFor(it);
          return `<div class="qr-wrap">${renderToStaticMarkup(
            <QRCodeSVG value={val} size={qrPx} level="M" includeMargin={false} />
          )}</div>`;
        }
        const value = resolveField(f, it);
        return `<div class="tpl-field"><span class="tpl-key">${f.field_name}:</span> <span class="tpl-val">${value}</span></div>`;
      });
      return `<div class="label"><div class="content col tpl">${blocks.join('')}</div></div>`;
    };

    const renderDefaultLabel = (it) => {
      const infoVal = infoUrlFor(it);
      const restockVal = restockQrValueFor(it);
      const safeName = (it.item_name || '').slice(0, 80);
      const vendorLine = it.vendor_sku ? `<div class="meta">Vendor SKU: ${it.vendor_sku}</div>` : '';
      const vendorLineDual = it.vendor_sku ? `<div class="dual-vendor">Vendor SKU: ${it.vendor_sku}</div>` : '';
      if (showDual) {
        // iter35: When BOTH QRs are printed, stack a centered SKU+name banner ABOVE the two QRs
        // so the product name has full width and isn't truncated.
        const dualQrPx = Math.round(size.height * 96 * 0.7); // smaller to leave room for header band
        const infoSvg = renderToStaticMarkup(<QRCodeSVG value={infoVal} size={dualQrPx} level="M" includeMargin={false} />);
        const restockSvg = renderToStaticMarkup(<QRCodeSVG value={restockVal} size={dualQrPx} level="M" includeMargin={false} />);
        return `
          <div class="label">
            <div class="dual-stack">
              <div class="dual-header">
                <div class="dual-sku">${it.item_sku}</div>
                <div class="dual-name">${safeName}</div>
                ${vendorLineDual}
              </div>
              <div class="dual-qrs">
                <div class="qr-cell"><div class="qr-label">INFO</div>${infoSvg}</div>
                <div class="qr-cell"><div class="qr-label">RESTOCK</div>${restockSvg}</div>
              </div>
            </div>
          </div>
        `;
      }
      // Single-QR layout (Info or Restock)
      const singleVal = showRestockOnly ? restockVal : infoVal;
      const singleLabel = showRestockOnly ? 'RESTOCK' : 'INFO';
      const singleSvg = renderToStaticMarkup(<QRCodeSVG value={singleVal} size={qrPx} level="M" includeMargin={false} />);
      const showCornerLabel = showRestockOnly; // mark Restock-only labels explicitly
      return `
        <div class="label">
          <div class="content ${isLandscape ? 'row' : 'col'}">
            <div class="qr-wrap">${singleSvg}${showCornerLabel ? `<div class="qr-mode-tag">${singleLabel}</div>` : ''}</div>
            <div class="text-wrap">
              <div class="sku">${it.item_sku}</div>
              <div class="name">${safeName}</div>
              ${vendorLine}
            </div>
          </div>
        </div>
      `;
    };

    const renderLabel = selectedTemplate ? renderTemplateLabel : renderDefaultLabel;
    const copiesPerItem = Math.max(1, parseInt(copies) || 1);
    const allLabels = itemList
      .flatMap(it => Array.from({ length: copiesPerItem }, () => renderLabel(it)))
      .join('');

    w.document.write(`<!doctype html><html><head><title>Label${isBulk ? 's' : ''} - ${previewItem.item_sku}</title>
      <style>
        @page { size: ${size.width}in ${size.height}in; margin: 0; }
        * { box-sizing: border-box; }
        html, body { margin: 0; padding: 0; font-family: 'IBM Plex Sans', system-ui, sans-serif; }
        .label {
          width: ${size.width}in;
          height: ${size.height}in;
          padding: ${pad}in;
          page-break-after: always;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .content { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; gap: ${Math.max(0.04, pad)}in; }
        .content.row { flex-direction: row; }
        .content.col { flex-direction: column; justify-content: center; align-items: center; text-align: center; }
        .qr-wrap { flex-shrink: 0; display: flex; align-items: center; justify-content: center; max-width: 100%; max-height: 100%; }
        .qr-wrap svg { width: ${baseQrIn}in; height: ${baseQrIn}in; max-width: 100%; max-height: 100%; display: block; }
        .qrs { flex-shrink: 0; display: flex; flex-direction: row; gap: 0.05in; }
        .qr-cell { display: flex; flex-direction: column; align-items: center; justify-content: center; }
        .qr-cell svg { width: ${baseQrIn * 0.85}in; height: ${baseQrIn * 0.85}in; max-width: 100%; max-height: 100%; display: block; }
        .qr-label { font-size: ${Math.max(5, size.width * 3)}pt; color: #475569; font-weight: 700; letter-spacing: 0.05em; text-align: center; }
        .qr-mode-tag { position: absolute; top: 0; right: 0; font-size: ${Math.max(4, size.width * 2.5)}pt; color: #ea580c; font-weight: 700; letter-spacing: 0.05em; }
        .qr-wrap { position: relative; }
        /* Dual-QR stacked layout — iter78w: name centered & wraps; never truncates */
        .dual-stack { width: 100%; height: 100%; display: flex; flex-direction: column; align-items: stretch; padding: 0; }
        .dual-header { width: 100%; text-align: center; padding: 0.05in 0.06in 0.03in; line-height: 1.1; }
        .dual-sku { font-family: 'JetBrains Mono', ui-monospace, monospace; font-weight: 800; font-size: ${Math.max(11, size.width * 6.5)}pt; word-break: break-all; line-height: 1.0; }
        .dual-name { font-size: ${Math.max(8, size.width * 3.6)}pt; color: #1e293b; font-weight: 600; margin-top: 0.04in; line-height: 1.1; word-wrap: break-word; overflow-wrap: break-word; white-space: normal; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
        .dual-vendor { font-size: ${Math.max(6, size.width * 2.8)}pt; color: #64748b; margin-top: 0.02in; }
        .dual-qrs { flex: 1; display: flex; flex-direction: row; align-items: center; justify-content: space-around; padding: 0 0.05in 0.05in; gap: 0.06in; min-height: 0; }
        .dual-qrs .qr-cell svg { width: ${Math.min(size.width * 0.42, size.height * 0.55)}in; height: ${Math.min(size.width * 0.42, size.height * 0.55)}in; max-width: 100%; max-height: 100%; display: block; }
        .text-wrap { flex: 1; min-width: 0; line-height: 1.15; overflow: hidden; text-align: ${isLandscape ? 'left' : 'center'}; }
        .content.col .text-wrap { text-align: center; }
        .sku { font-family: 'JetBrains Mono', ui-monospace, monospace; font-weight: 800; font-size: ${isLandscape ? Math.max(11, innerH * 18) : Math.max(11, innerW * 11)}pt; word-break: break-word; overflow: hidden; text-overflow: ellipsis; line-height: 1.05; }
        .name { font-size: ${isLandscape ? Math.max(7, innerH * 9) : Math.max(7, innerW * 6)}pt; color: #334155; margin-top: 0.03in; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; line-height: 1.15; }
        .meta { font-size: ${Math.max(6, innerW * 4.5)}pt; color: #475569; font-weight: 600; margin-top: 0.03in; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .content.tpl { flex-direction: column; align-items: stretch; gap: 0.04in; padding: 0.05in; }
        .tpl-field { font-size: ${Math.max(7, size.width * 5)}pt; color: #1e293b; line-height: 1.2; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .tpl-key { font-weight: 600; color: #475569; margin-right: 0.04in; }
        .tpl-val { font-family: 'JetBrains Mono', ui-monospace, monospace; }
        .content.tpl .qr-wrap { align-self: center; margin: 0.04in 0; }
      </style></head><body>
        ${allLabels}
        <script>
          window.onload = function () {
            window.focus();
            window.print();
            window.onafterprint = function(){ window.close(); };
          };
        </script>
      </body></html>`);
    w.document.close();
  };

  // Preview scaling: show 96 px per inch capped to fit dialog
  const PREVIEW_PX_PER_IN = 64;
  const previewW = size.width * PREVIEW_PX_PER_IN;
  const previewH = size.height * PREVIEW_PX_PER_IN;
  const qrPreviewSize = isLandscape
    ? Math.floor(size.height * PREVIEW_PX_PER_IN * 0.85)
    : Math.floor(size.width * PREVIEW_PX_PER_IN * 0.6);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="print-label-dialog">
        <DialogHeader>
          <DialogTitle>
            {isBulk
              ? `Print ${itemList.length} labels`
              : `Print Label — ${previewItem.item_sku}`}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {isBulk && (
            <div className="rounded-sm bg-orange-50 border border-orange-200 px-3 py-2 text-xs text-orange-700" data-testid="bulk-print-info">
              Printing {itemList.length} item{itemList.length === 1 ? '' : 's'} × {Math.max(1, parseInt(copies) || 1)} cop{(parseInt(copies) || 1) === 1 ? 'y' : 'ies'} each.
              Preview shows the first item.
            </div>
          )}
          <div className="space-y-2" data-testid="template-picker-row">
            <Label>Label Template</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger className="rounded-sm" data-testid="label-template-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default" data-testid="label-template-option-default">
                  Default ({labelType} layout)
                </SelectItem>
                {templates
                  .filter((t) => !labelType || t.label_type === labelType)
                  .map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`label-template-option-${t.id}`}>
                      {t.template_name} <span className="text-xs text-slate-400 ml-1">({t.fields?.length || 0} fields)</span>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {templates.filter((t) => !labelType || t.label_type === labelType).length === 0 && (
              <p className="text-xs text-slate-500">
                No saved {labelType} templates. Create one in <span className="font-medium">Label Templates</span>.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Label Size</Label>
            <Select value={sizeId} onValueChange={setSizeId}>
              <SelectTrigger className="rounded-sm" data-testid="label-size-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STICKER_SIZES.map(s => (
                  <SelectItem key={s.id} value={s.id} data-testid={`label-size-option-${s.id}`}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Copies</Label>
            <Input
              type="number"
              min={1}
              max={50}
              value={copies}
              onChange={(e) => setCopies(e.target.value)}
              className="rounded-sm w-24 mono"
              data-testid="label-copies-input"
            />
          </div>

          <div className="space-y-2 rounded-sm border border-slate-200 p-3" data-testid="qr-mode-row">
            <Label className="text-sm">QR Code Layout</Label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'info', label: 'Info QR', sub: 'Single QR → product page' },
                { value: 'restock', label: 'Restock QR', sub: 'Single QR → quick restock', requiresUrl: true },
                { value: 'both', label: 'Both', sub: 'Two QRs · name centered above', requiresLandscape: true },
              ].map((opt) => {
                const disabled =
                  (opt.requiresUrl && !infoQrValue.startsWith('http')) ||
                  (opt.requiresLandscape && !dualSupported);
                const selected = qrMode === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={disabled}
                    onClick={() => setQrMode(opt.value)}
                    className={`text-left p-2 rounded-sm border transition ${selected ? 'border-orange-500 bg-orange-50 ring-1 ring-orange-300' : 'border-slate-200 hover:border-slate-300'} ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                    data-testid={`qr-mode-${opt.value}`}
                  >
                    <div className={`text-sm font-medium ${selected ? 'text-orange-700' : 'text-slate-800'}`}>{opt.label}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">{opt.sub}</div>
                    {disabled && <div className="text-[9px] text-slate-400 mt-1">{opt.requiresLandscape ? 'Needs landscape ≥ 2″' : 'Needs URL QR'}</div>}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Preview</Label>
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 flex justify-center">
              <div
                className="bg-white border border-dashed border-slate-300 flex items-center justify-center"
                style={{ width: `${previewW}px`, height: `${previewH}px`, padding: '6px' }}
                data-testid="label-preview"
              >
                {showDual ? (
                  // iter35: Both-QRs preview — centered SKU/name banner above two QRs
                  <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', gap: 4, padding: 2 }}>
                    <div style={{ textAlign: 'center', lineHeight: 1.05 }}>
                      <div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 800, fontSize: 16, wordBreak: 'break-all' }}>
                        {previewItem.item_sku}
                      </div>
                      <div style={{ fontSize: 10, color: '#334155', overflow: 'hidden', maxHeight: 26, marginTop: 2 }}>
                        {(previewItem.item_name || '').slice(0, 60)}
                      </div>
                      {previewItem.vendor_sku && (
                        <div style={{ fontSize: 8, color: '#475569', fontWeight: 600, marginTop: 2 }}>Vendor SKU: {previewItem.vendor_sku}</div>
                      )}
                    </div>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ fontSize: 6, color: '#475569', fontWeight: 700, letterSpacing: '0.05em' }}>INFO</div>
                        <QRCodeSVG value={infoQrValue} size={Math.floor(Math.min(previewW, previewH) * 0.42)} level="M" includeMargin={false} />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ fontSize: 6, color: '#475569', fontWeight: 700, letterSpacing: '0.05em' }}>RESTOCK</div>
                        <QRCodeSVG value={restockQrValue} size={Math.floor(Math.min(previewW, previewH) * 0.42)} level="M" includeMargin={false} />
                      </div>
                    </div>
                  </div>
                ) : (
                  // Single-QR preview (Info or Restock)
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: isLandscape ? 'row' : 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                      width: '100%',
                      height: '100%',
                      textAlign: isLandscape ? 'left' : 'center',
                    }}
                  >
                    <div style={{ position: 'relative' }}>
                      <QRCodeSVG value={primaryQrValue} size={qrPreviewSize} level="M" includeMargin={false} />
                      {showRestockOnly && (
                        <div style={{ position: 'absolute', top: -2, right: -2, fontSize: 7, color: '#ea580c', fontWeight: 700 }}>{primaryQrLabel}</div>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0, lineHeight: 1.05, overflow: 'hidden' }}>
                      <div style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        fontWeight: 800,
                        fontSize: isLandscape ? 18 : 14,
                        wordBreak: 'break-all',
                      }}>
                        {previewItem.item_sku}
                      </div>
                      <div style={{ fontSize: isLandscape ? 11 : 10, color: '#334155', marginTop: 2 }}>
                        {(previewItem.item_name || '').slice(0, 40)}
                      </div>
                      {previewItem.vendor_sku && (
                        <div style={{ fontSize: 9, color: '#475569', fontWeight: 600, marginTop: 2 }}>Vendor SKU: {previewItem.vendor_sku}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Page size will be set to {size.width}″ × {size.height}″ when printing.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">
            Cancel
          </Button>
          <Button
            onClick={handlePrint}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
            data-testid="print-label-confirm-btn"
          >
            <Printer className="w-4 h-4 mr-2" />
            {isBulk ? `Print ${itemList.length * Math.max(1, parseInt(copies) || 1)} labels` : 'Print'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PrintLabelDialog;
